import "../../../App.css";
import React, {useCallback, useEffect, useState} from "react";
import {Button, Descriptions, Form, Input, Layout, message, Modal, Radio, Result, Typography} from "antd";
import "antd/dist/reset.css";
import useAxios from "axios-hooks";
import {CheckoutSubmissionRequest} from "../../../types/CheckoutSubmissionRequest";
import {useOrderId} from "../../../hooks/UseOrderId";
import {PaymentType} from "../../../App";
import {CardTypeEnum} from "../../../types/CardTypeEnum";
import {CheckoutStatusResult} from "../../../types/CheckoutStatusResult";
import {ProviderTypeEnum} from "../../../types/ProviderTypeEnum";
import WrappedCard from "../../antd/WrappedCard";
import easypaisaLogo from "../../../assets/easypaisa-logo3.png";
import jazzcashLogo from "../../../assets/jazzcash-logo2.png";
import bankCardLogo from "../../../assets/bankcard-logo.png";
import "./PkRouteSettingFirstpayMode.css";
import IntentAppFirstPay from "../shared/IntentAppFirstPay";
import teachingVideo from "../../../assets/bankCardTeching.mp4";
import {Copyable} from "../../common/Copyable";
import loadingGif from "../../../assets/background/share/loading.gif";

const {Content} = Layout;

const ignore = false;
const OverdueTime = 3 * 60 * 1000;
const PollingTime = 5 * 1000;

// NOTICE: GET目前是同步的，后面有可能会需要支援非同步，近来收银台后先转圈等到GET有结果
function PkRouteSettingFirstpayMode({getCheckoutInfoData}: PaymentType) {
    const id = useOrderId();

    const [{data: putData}, executePost] =
        useAxios<CheckoutSubmissionRequest>(
            {
                url: `/v2/checkout/${id}`,
                method: "POST"
            },
            {manual: true}
        );
    // console.log("[POST] checkout.putData", putData);

    const executePostWithRetry = async (postData: any) => {
        const retries = 3;
        const delay = 3000;
        for (let i = 0; i < retries; i++) {
            try {
                return await executePost(postData);
            } catch (error) {
                if (i < retries - 1) {
                    console.warn(`[POST RETRY] Attempt ${i + 1} failed, retrying in ${delay}ms...`, error);
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    console.error("[POST RETRY] All retry attempts failed.");
                    throw error;
                }
            }
        }
    };

    const [
        {
            data: getCheckoutResultData,
            loading: pollingLoading
        },
        pollingCheckoutResult
    ] = useAxios<CheckoutStatusResult>(`/v2/orders/${id}`);

    const [submitCheckout, setSubmitCheckout] = useState(getCheckoutInfoData?.checkoutStatus === "SUBMITTED");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [providerName] = useState(
        getCheckoutInfoData?.defaultChannel === "easypaisa" ? CardTypeEnum.EasyPaisa :
            getCheckoutInfoData?.defaultChannel === "jazzcash" ? CardTypeEnum.JazzCash :
                getCheckoutInfoData?.defaultChannel === "card_group:bank" ? CardTypeEnum.BankCard :
                    getCheckoutInfoData?.defaultChannel === "card_group:sadapay" ? CardTypeEnum.SadapayCard :
                        getCheckoutInfoData?.defaultChannel === "card_group:opay" ? CardTypeEnum.OpayCard :
                            null);
    const [form] = Form.useForm();
    const [isFormValid, setIsFormValid] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [showBankCardHintModal, setShowBankCardHintModal] = useState(false);
    const [showIntentAppHintModal, setShowIntentAppHintModal] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(getCheckoutInfoData?.automaticSubmission);

    // 是否是钱包
    const [isWallet, setIsWallet] = useState(false);
    const [isJazzcash, setIsJazzcash] = useState(false);
    const [isEasypaisa, setIsEasypaisa] = useState(false);

    // 是否是卡片
    const [isCard, setIsCard] = useState(false);
    const [isBankCard, setIsBankCard] = useState(false);
    const [isSadapayCard, setIsSadapayCard] = useState(false);
    const [isOpayCard, setIsOpayCard] = useState(false);

    const [neverCountDown, setNeverCountDown] = useState(true);
    const [countDownText, setCountDownText] = useState("Ready countdown");

    const onFormFinish = (values: any) => {
        setSubmitCheckout(true);

        // NOTICE: 三分截止
        startOverdueCountDown();

        if (!ignore) {
            let submitFieldType;
            switch (values.providerName) {
                case CardTypeEnum.EasyPaisa:
                    submitFieldType = getCheckoutInfoData?.easypaisaSubmitFieldType;
                    break;
                case CardTypeEnum.JazzCash:
                    submitFieldType = getCheckoutInfoData?.jazzcashSubmitFieldType;
                    break;
                default:
                    submitFieldType = "VOID";
                    break;
            }

            executePostWithRetry({
                data: {
                    id: id,
                    providerType: ProviderTypeEnum.WALLET,
                    providerName: values.providerName,
                    customerAccount: values.customerAccount,
                    customerIdCard: values.customerIdCard,
                    customerName: values.customerName,
                    reserved1: getCheckoutInfoData?.reserved1,
                    reserved2: getCheckoutInfoData?.reserved2,
                    automaticSubmission:getCheckoutInfoData?.automaticSubmission,
                    submitFieldType: submitFieldType
                }
            })
                .then(() => {
                    setSubmitCheckout(true);
                })
                .catch((error) => {
                    messageApi.error("Failed:" + error?.response?.data?.title);
                })
                .finally(() => {
                });
        }
    };

    const onButtonClick = () => {
        setIsSubmitting(true);
        form.submit();
        setShowIntentAppHintModal(true);
    };

    const checkFormValidity = () => {
        form.validateFields()
            .then(() => {
                setIsFormValid(true);
            })
            .catch(() => {
                setIsFormValid(false);
            });
    }

    const refreshPayType = () => {
        refreshField();
        checkFormValidity();
    }

    // 依照选项显示不同的内容
    const refreshField = () => {
        let cardType = form.getFieldValue("providerName");

        setIsWallet(false);
        setIsJazzcash(false);
        setIsEasypaisa(false);

        setIsCard(false);
        setIsBankCard(false);
        setIsSadapayCard(false);
        setIsOpayCard(false);

        if (cardType === CardTypeEnum.JazzCash){
            setIsWallet(true);
            setIsJazzcash(true);
        }else if (cardType === CardTypeEnum.EasyPaisa){
            setIsWallet(true);
            setIsEasypaisa(true);
        }else if (cardType === CardTypeEnum.BankCard){
            setIsCard(true);
            setIsBankCard(true);
        }else if (cardType === CardTypeEnum.SadapayCard){
            setIsCard(true);
            setIsSadapayCard(true);
        }else if (cardType === CardTypeEnum.OpayCard){
            setIsCard(true);
            setIsOpayCard(true);
        }
    }

    const submitCountDown = () => {
        let num = 30 * 60;
        setCountDownText(formatTime(num));
        let timer = setInterval(() => {
            num--;
            setCountDownText(formatTime(num));
            if (num === 0) {
                setCountDownText("Time's Up !!!");
                clearInterval(timer);
            }
        }, 1000);
    };

    const formatTime = (seconds: number) => {
        let minutes = Math.floor(seconds / 60);
        let remainingSeconds = seconds % 60;
        return `${padZero(minutes)}:${padZero(remainingSeconds)}`;
    };

    const padZero = (num: number) => {
        return num < 10 ? `0${num}` : `${num}`;
    };

    const [isOverdueTime, setIsOverdueTime] = useState<boolean>(false);
    // console.log("0.isOverdueTime", isOverdueTime);

    // NOTICE: 十秒輪詢 way1
    useEffect(() => {
        const stoppingStatuses: string[] = ["SUCCESS", "FAIL"]

        let pollingCountDown: NodeJS.Timeout;
        if (isOverdueTime) {
            console.log(
                "[Polling] Cause overdue time , so no need to process to poll"
            );
        } else if (!submitCheckout) {
            refreshPayType();
            console.log("[Polling] no need to process to poll");
        } else {
            console.log("[Polling] pollingLoading", pollingLoading);
            if (getCheckoutResultData?.status === "PROCESSING") {
                console.log("[Polling] restart to polling");

                if (neverCountDown){
                    setNeverCountDown(false);
                    submitCountDown();
                }

                pollingCountDown = setTimeout(function () {
                    pollingCheckoutResult()
                        .then((data) => {
                            console.log("[Polling] end to polling.data", data);
                        })
                        .catch((error) => {
                            console.log("[Polling] restart to polling.error", error);
                        })
                        .finally(() => {
                        });
                }, PollingTime);
            } else if (typeof getCheckoutResultData !== "undefined" && stoppingStatuses.includes(getCheckoutResultData.status)) {
                console.log("[Polling] end to polling.data", getCheckoutResultData);
                messageApi.info("Result: " + getCheckoutResultData.status);
            }
        }
        return () => {
            if (pollingCountDown) clearTimeout(pollingCountDown);
        };
    }, [submitCheckout, pollingLoading, isOverdueTime, getCheckoutResultData]);

    useEffect(() => {
        if (!submitCheckout && isSubmitted) {
            setIsSubmitted(false);
            setShowIntentAppHintModal(true);
            onFormFinish({
                providerName: getCheckoutInfoData?.defaultChannel,
                customerAccount: getCheckoutInfoData?.receiverAccount,
                customerIdCard: getCheckoutInfoData?.receiverIdCard,
                customerName: getCheckoutInfoData?.receiverName,
            });
        }
    }, [isSubmitted]);

    // NOTICE: 三分截止
    let countDown: NodeJS.Timeout;
    const startOverdueCountDown = useCallback(() => {
        console.log("[CountDown] start to count down");
        countDown = setTimeout(function () {
            console.log("[CountDown] end to count down");
            setIsOverdueTime(true);
        }, OverdueTime);
    }, []);

    const clearOverdueCountDown = () => {
        clearTimeout(countDown);
    };
    useEffect(() => {
        return () => {
            clearOverdueCountDown();
        };
    });

    return (
        <>
            {contextHolder}
            {/*NOTICE: 訂單未完成*/}
            {getCheckoutInfoData?.checkoutStatus === "VISITED" &&
                !submitCheckout && (
                    <Content>
                        <header className="rs2-header">
                            <div className="rs2-header-font">Deposit</div>
                            <div className="rs2-amount-container">
                                <div className="rs2-amount-content">
                                    <div className="rs2-amount">
                                        Rs {getCheckoutInfoData?.orderAmount}.00
                                    </div>
                                    <div className="rs2-amount-currency">
                                        Payment Amount
                                    </div>
                                    <div className="rs2-amount-currency-arabic">
                                        ادائیگی کی رقم
                                    </div>
                                </div>
                            </div>
                        </header>

                        <WrappedCard cardProps={{className: "rs2-payment-container"}}>
                            <Form
                                form={form}
                                initialValues={{providerName}}
                                onFinish={onFormFinish}
                                onChange={refreshPayType}
                            >
                                <>
                                    <div className="rs2-form-amount">
                                        <span className="rs2-form-amount-currency">Payment Type</span>
                                        <span className="rs2-form-amount-currency-arabic">ادائیگی کی قسم</span>
                                    </div>

                                    <Form.Item name="providerName" className="rs2-form-item">
                                        <Radio.Group className="rs2-radio-group" name="providerName">
                                            {getCheckoutInfoData?.availableChannels.includes("easypaisa") && (
                                                <Radio.Button value={CardTypeEnum.EasyPaisa}
                                                              className="rs2-radio-button">
                                                    <div className="rs2-form-item-img">
                                                        <img src={easypaisaLogo} alt="Easypaisa"
                                                             className="rs2-form-item-img-easypaisa"/>
                                                        <span className="rs2-image-text-easypaisa">easypaisa</span>
                                                    </div>
                                                </Radio.Button>)}
                                            {getCheckoutInfoData?.availableChannels.includes("jazzcash") && (
                                                <Radio.Button value={CardTypeEnum.JazzCash}
                                                              className="rs2-radio-button">
                                                    <div className="rs2-form-item-img">
                                                        <img src={jazzcashLogo} alt="Jazzcash"
                                                             className="rs2-form-item-img-jazzcash"/>
                                                        <span className="rs2-image-text-jazzcash">jazzcash</span>
                                                    </div>
                                                </Radio.Button>)}
                                            {getCheckoutInfoData?.availableChannels.includes("card_group:bank") &&
                                                (<Radio.Button value={CardTypeEnum.BankCard}
                                                               className="rs2-radio-button">
                                                    <div className="rs2-form-item-img">
                                                        <img src={bankCardLogo} alt="card"
                                                             className="rs2-form-item-img-card"/>
                                                        <span className="rs2-image-text-card">bank card</span>
                                                    </div>
                                                </Radio.Button>)}
                                            {getCheckoutInfoData?.availableChannels.includes("card_group:sadapay") &&
                                                (<Radio.Button value={CardTypeEnum.SadapayCard}
                                                               className="rs2-radio-button">
                                                    <div className="rs2-form-item-img">
                                                        <img src={bankCardLogo} alt="card"
                                                             className="rs2-form-item-img-card"/>
                                                        <span className="rs2-image-text-card">sadapay</span>
                                                    </div>
                                                </Radio.Button>)}
                                            {getCheckoutInfoData?.availableChannels.includes("card_group:opay") &&
                                                (<Radio.Button value={CardTypeEnum.OpayCard}
                                                               className="rs2-radio-button">
                                                    <div className="rs2-form-item-img">
                                                        <img src={bankCardLogo} alt="card"
                                                             className="rs2-form-item-img-card"/>
                                                        <span className="rs2-image-text-card">opay</span>
                                                    </div>
                                                </Radio.Button>)}
                                        </Radio.Group>
                                    </Form.Item>
                                    <div className="rs2-form-container">
                                        <div className="rs2-input-container-demo">
                                            <hr className="rs2-hr"/>
                                        </div>
                                    </div>

                                    <div className="rs2-form-container">
                                        <div className="rs2-input-container">
                                            {/*银行卡收说明*/}
                                            {isBankCard && (
                                                <WrappedCard cardTitle="Information">
                                                    <>
                                                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                            Step 1. Fill in payment information <br/>
                                                            Step 2. Pay within 30 minutes <br/>
                                                            Step 3. Please transfer fund to the following bank
                                                            account <br/>
                                                            <Button style={{
                                                                fontSize: 16,
                                                                color: "white",
                                                                background: "#1677ff",
                                                                textAlign: "center"
                                                            }} onClick={() => {
                                                                setShowBankCardHintModal(true)
                                                            }}>How to do it?</Button>
                                                            <Modal
                                                                open={showBankCardHintModal}
                                                                closable={false}
                                                                onOk={() => {
                                                                    setShowBankCardHintModal(false)
                                                                }}
                                                                onCancel={() => {
                                                                    setShowBankCardHintModal(false)
                                                                }}
                                                            >
                                                                <video controls width="100%">
                                                                    <source src={teachingVideo} type="video/mp4"/>
                                                                    Your browser does not support the video tag.
                                                                </video>
                                                            </Modal>
                                                            <br/>
                                                        </div>
                                                    </>
                                                </WrappedCard>
                                            )}

                                            {/* Sadapay卡收说明*/}
                                            {isSadapayCard && (
                                                <WrappedCard cardTitle="Information">
                                                    <>
                                                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                            Step 1. Fill in payment information <br/>
                                                            Step 2. Pay within 30 minutes <br/>
                                                            Step 3. Please transfer fund to the following SadaPay
                                                            account <br/>
                                                        </div>
                                                    </>
                                                </WrappedCard>
                                            )}

                                            {/* Opay卡收说明*/}
                                            {isOpayCard && (
                                                <WrappedCard cardTitle="Information">
                                                    <>
                                                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                                                            Step 1. Fill in payment information <br/>
                                                            Step 2. Pay within 30 minutes <br/>
                                                            Step 3. Please transfer fund to the following OPay
                                                            account <br/>
                                                        </div>
                                                    </>
                                                </WrappedCard>
                                            )}

                                            {((isEasypaisa && getCheckoutInfoData?.easypaisaSubmitFields.includes("customerAccount")) ||
                                                (isJazzcash && getCheckoutInfoData?.jazzcashSubmitFields.includes("customerAccount"))) && (
                                                <>
                                                    <div className="rs2-input-label">
                                                    <span
                                                        className="rs2-form-amount-currency">Wallet Account Number</span>
                                                        <span className="rs2-form-amount-currency-arabic">والیٹ اکاؤنٹ نمبر</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerAccount"
                                                        className="rs2-form-rules-fail"
                                                        rules={[{
                                                            required: true,
                                                            message: "account should be an integer starting with 3 and totaling ten digits."
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverAccount}
                                                    >
                                                        <Input className="rs2-input-field"
                                                               placeholder="eg. 0000-0000000"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            {((isEasypaisa && getCheckoutInfoData?.easypaisaSubmitFields.includes("customerIdCard")) ||
                                                (isJazzcash && getCheckoutInfoData?.jazzcashSubmitFields.includes("customerIdCard"))) && (
                                                <>
                                                    <div className="rs2-input-label">
                                                        <span className="rs2-form-amount-currency">CNIC</span>
                                                        <span
                                                            className="rs2-form-amount-currency-arabic">شناختی کارڈ</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerIdCard"
                                                        className="rs2-form-rules-fail"
                                                        rules={[{
                                                            required: true,
                                                            message: "CNIC should be real CNIC number and thirteen digital integer."
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverIdCard}
                                                    >
                                                        <Input className="rs2-input-field"
                                                               placeholder="eg. 35134-0000000-0"/>
                                                    </Form.Item>
                                                </>
                                            )}

                                            {isCard && (
                                                <>
                                                    <div className="rs2-input-label">
                                                    <span
                                                        className="rs2-form-amount-currency">Account Name</span>
                                                        <span className="rs2-form-amount-currency-arabic">اکاؤنٹ کا نام</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerName"
                                                        className="rs2-form-rules-fail"
                                                        rules={[{
                                                            required: true,
                                                            message: "Please insert correct name"
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverName}
                                                    >
                                                        <Input className="rs2-input-field"
                                                               placeholder="eg. 0000-0000000"/>
                                                    </Form.Item>

                                                    <div className="rs2-input-label">
                                                    <span
                                                        className="rs2-form-amount-currency">Bank Account Number or Wallet Account</span>
                                                        <span className="rs2-form-amount-currency-arabic">بینک اکاؤنٹ نمبر یا والیٹ اکاؤنٹ</span>
                                                    </div>
                                                    <Form.Item
                                                        name="customerAccount"
                                                        className="rs2-form-rules-fail"
                                                        rules={[{
                                                            required: true,
                                                            message: "Please insert correct account"
                                                        }]}
                                                        initialValue={getCheckoutInfoData?.receiverAccount}
                                                    >
                                                        <Input className="rs2-input-field"
                                                               placeholder="eg. 35134-0000000-0"/>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </div>

                                        <Form.Item>
                                            <div className="rs2-submit-button-container">
                                                <Button type="primary"
                                                        className="rs2-submit-button"
                                                        onClick={onButtonClick}
                                                        disabled={!isFormValid || isSubmitting}>
                                                    {isSubmitting ? (
                                                        <>
                                                            <img
                                                                src={loadingGif}
                                                                alt="loading"
                                                                style={{width: "24px", height: "24px"}}
                                                            />
                                                            <span className="rs2-submit-button-font">Continue</span>
                                                        </>
                                                    ) : (<span className="rs2-submit-button-font">Continue</span>)}
                                                </Button>
                                            </div>
                                        </Form.Item>
                                    </div>
                                </>
                            </Form>
                            <p className="rs2-footer-text">
                                After the transaction please open your wallet
                            </p>
                            <p className="rs2-footer-text-arabic">
                                لین دین کے بعد براہ کرم اپنا بٹوہ کھولیں۔
                            </p>
                        </WrappedCard>
                    </Content>
                )}

            {submitCheckout && showIntentAppHintModal && isWallet &&
                <IntentAppFirstPay providerName={form.getFieldValue("providerName")}/>}

            {/*卡收提交后画面*/}
            {submitCheckout && isCard && !isOverdueTime && getCheckoutResultData?.status === "PROCESSING" && (
                    <Result
                        status="info"
                        title={
                            <>
                                <div style={{textAlign: "center", color: "red"}}>
                                    Time Remaining<br/>
                                    {countDownText}
                                </div>
                                <div>
                                    Please pay within 30 minutes.<br/>
                                </div>
                            </>
                        }
                    />
                )}

            {/*银行卡收说明*/}
            {submitCheckout && isBankCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following bank
                            account <br/>
                            <Button style={{
                                fontSize: 16,
                                color: "white",
                                background: "#1677ff",
                                textAlign: "center"
                            }} onClick={() => {
                                setShowBankCardHintModal(true)
                            }}>How to do it?</Button>
                            <Modal
                                open={showBankCardHintModal}
                                closable={false}
                                onOk={() => {
                                    setShowBankCardHintModal(false)
                                }}
                                onCancel={() => {
                                    setShowBankCardHintModal(false)
                                }}
                            >
                                <video controls width="100%">
                                    <source src={teachingVideo} type="video/mp4"/>
                                    Your browser does not support the video tag.
                                </video>
                            </Modal>
                            <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {/* Sadapay卡收说明*/}
            {submitCheckout && isSadapayCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following SadaPay
                            account <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {/* Opay卡收说明*/}
            {submitCheckout && isOpayCard && (
                <WrappedCard cardTitle="Information">
                    <>
                        <div style={{fontSize: 16, color: "blue", width: "100%"}}>
                            Step 1. Fill in payment information <br/>
                            Step 2. Pay within 30 minutes <br/>
                            Step 3. Please transfer fund to the following OPay account <br/>
                        </div>
                    </>
                </WrappedCard>
            )}

            {submitCheckout && isCard && (
                <Descriptions title={
                    <>
                        <table width="100%" className="rs2-custom-table">
                            <tbody>
                            <tr>
                                <td className="rs2-form-amount-currency">Card Information</td>
                                <td align="right" className="rs2-form-amount-currency-arabic">بینک کی معلومات</td>
                            </tr>
                            </tbody>
                        </table>
                    </>
                } bordered>
                    <Descriptions.Item label={
                        <>
                            Amount<br/>
                            قابل ادائیگی رقم
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.orderAmount}
                        </Typography.Title>
                    </Descriptions.Item>

                    <Descriptions.Item label={
                        <>
                            Bank Account<br/>
                            بینک اکاؤنٹ
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverAccount}
                        </Typography.Title>
                    </Descriptions.Item>

                    <Descriptions.Item label={
                        <>
                            Bank Name<br/>
                            بینک کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverBankName}
                        </Typography.Title>
                    </Descriptions.Item>

                    {getCheckoutInfoData?.receiverBranchBank && (<Descriptions.Item label={
                        <>
                            Branch Bank Name<br/>
                            بینک برانچ کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverBankBranchName}
                        </Typography.Title>
                    </Descriptions.Item>)}

                    <Descriptions.Item label={
                        <>
                            Account Name<br/>
                            کھاتے کا نام
                        </>
                    }>
                        <Typography.Title
                            copyable={{icon: <Copyable/>}}
                            level={5}
                            style={{margin: 0}}
                        >
                            {getCheckoutInfoData?.cardReceiverName}
                        </Typography.Title>
                    </Descriptions.Item>
                </Descriptions>
            )}
        </>
    );
}

export default PkRouteSettingFirstpayMode;
